import { useQuery } from '@tanstack/react-query';
import {
  Calendar,
  ChevronDown,
  ChevronUp,
  CreditCard,
  Eye,
  Loader2,
  Search,
  X,
} from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { partnerApi, type BillableEvent } from '../lib/api';
import { PartnerResourceModal } from './PartnerResourceModal.js';
import { Button } from './ui/button';
import { Input } from './ui/input';

interface TransactionsTableProps {
  tenantId: string;
}

export const TransactionsTable: React.FC<TransactionsTableProps> = ({
  tenantId,
}) => {
  const [searchParams] = useSearchParams();
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCursors, setPageCursors] = useState<{
    [page: number]: string | undefined;
  }>({});
  const [selectedTransaction, setSelectedTransaction] =
    useState<BillableEvent | null>(null);
  const [showResourceModal, setShowResourceModal] = useState(false);
  const [selectedSchemaId, setSelectedSchemaId] = useState<string>('');
  const [sortField, setSortField] = useState<string>('created_date');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Initialize search term from URL parameters
  useEffect(() => {
    const searchQuery = searchParams.get('search');
    if (searchQuery) {
      setSearchTerm(searchQuery);
      setDebouncedSearchTerm(searchQuery);
    }
  }, [searchParams]);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 700); // 700ms delay

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch schema mappings to get transaction schemas
  const { data: schemaMappings } = useQuery({
    queryKey: ['schemaMappings'],
    queryFn: () => partnerApi.getSchemaMappings(),
  });

  // Get transaction schemas from mappings
  const transactionSchemas = schemaMappings?.transactionsSchema || [];

  const {
    data: billableEventsResponse,
    isLoading,
    error,
  } = useQuery({
    queryKey: [
      'tenant-billable-events',
      tenantId,
      debouncedSearchTerm,
      startDate,
      endDate,
      currentPage,
      pageCursors[currentPage], // Include cursor in query key for proper cache invalidation
      sortField,
      sortDirection,
    ],
    queryFn: () =>
      partnerApi.getTenantBillableEvents(tenantId, {
        limit: 20,
        cursor: pageCursors[currentPage],
        searchTerm: debouncedSearchTerm || undefined,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        sortField: sortField || undefined,
        sortDirection: sortDirection || undefined,
      }),
    enabled: !!tenantId,
    staleTime: 30000, // Consider data fresh for 30 seconds
    gcTime: 300000, // Keep in cache for 5 minutes
  });

  // Store cursor for next page when data changes
  useEffect(() => {
    if (billableEventsResponse?.data?.pagination.cursor) {
      setPageCursors(prev => ({
        ...prev,
        [currentPage + 1]:
          billableEventsResponse.data.pagination.cursor || undefined,
      }));
    }
  }, [billableEventsResponse, currentPage]);

  // Reset state when tenantId changes (navigation between tenants)
  useEffect(() => {
    setCurrentPage(1);
    setPageCursors({});
  }, [tenantId]);

  const billableEvents = billableEventsResponse?.data?.data || [];
  const pagination = billableEventsResponse?.data?.pagination;

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    // Search is now handled by debouncedSearchTerm
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStartDate('');
    setEndDate('');
    setSortField('created_date');
    setSortDirection('desc');
  };

  // Validate date range
  const isDateRangeValid =
    !startDate || !endDate || new Date(startDate) <= new Date(endDate);

  // Reset pagination when search term, filters, or sorting change
  useEffect(() => {
    setCurrentPage(1);
    setPageCursors({});
  }, [debouncedSearchTerm, startDate, endDate, sortField, sortDirection]);

  // Reset sorting to default when date filters change
  useEffect(() => {
    if (startDate || endDate) {
      setSortField('created_date');
      setSortDirection('desc');
    }
  }, [startDate, endDate]);

  const handleSort = (field: string) => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // New field, default to ascending
      setSortField(field);
      setSortDirection('asc');
    }

    // Reset pagination immediately when sort changes
    setCurrentPage(1);
    setPageCursors({});
  };

  const getSortIcon = (field: string) => {
    if (sortField !== field) {
      return <ChevronUp className="w-4 h-4 text-gray-300" />;
    }
    return sortDirection === 'asc' ? (
      <ChevronUp className="w-4 h-4 text-gray-600" />
    ) : (
      <ChevronDown className="w-4 h-4 text-gray-600" />
    );
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const handleNextPage = () => {
    if (pagination?.hasNextPage) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handleViewResource = async (transaction: BillableEvent) => {
    setSelectedTransaction(transaction);
    setShowResourceModal(true);

    // If there are multiple schemas, try to find the one that works
    if (transactionSchemas.length > 1) {
      setSelectedSchemaId(''); // Clear selection to trigger the search

      // Try each schema until one succeeds
      for (const schema of transactionSchemas) {
        try {
          await partnerApi.getPartnerResourceByExternalId(
            tenantId,
            transaction.id,
            schema.schemaId,
          );
          // If successful, set this schema and break
          setSelectedSchemaId(schema.schemaId);
          break;
        } catch (error) {
          // Continue to next schema if this one fails
          console.log(
            `Schema ${schema.schemaId} failed for transaction ${transaction.id}:`,
            error,
          );
        }
      }
    } else if (transactionSchemas.length === 1) {
      // Single schema, use it directly
      setSelectedSchemaId(transactionSchemas[0].schemaId);
    }
  };

  const formatAmount = (amount: string, currency: string) => {
    const numAmount = parseFloat(amount);
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency || 'USD',
    }).format(numAmount);
  };

  if (error) {
    return (
      <div className="bg-red-50 p-4 rounded-lg">
        <p className="text-red-700">
          Error loading transactions:{' '}
          {error instanceof Error ? error.message : 'Unknown error'}
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col lg:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
            <Input
              type="text"
              placeholder="Search by transaction ID..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
        </form>
        <Input
          type="date"
          placeholder="Start Date"
          value={startDate}
          onChange={e => setStartDate(e.target.value)}
          className="w-full lg:w-auto"
        />
        <Input
          type="date"
          placeholder="End Date"
          value={endDate}
          onChange={e => setEndDate(e.target.value)}
          className={`w-full lg:w-auto ${!isDateRangeValid ? 'border-red-300 focus:border-red-500' : ''}`}
        />
        <Button
          type="button"
          variant="outline"
          onClick={clearFilters}
          className="w-full lg:w-auto">
          <X className="w-4 h-4 mr-1" />
          Clear Filters
        </Button>
      </div>

      {/* Date Range Validation Message */}
      {!isDateRangeValid && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-red-700">
            End date must be after or equal to start date.
          </p>
        </div>
      )}

      {/* Table */}
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-primary" />
          <span className="ml-2 text-sm text-gray-600">
            Loading transactions...
          </span>
        </div>
      ) : billableEvents.length === 0 ? (
        <div className="text-center py-8">
          <CreditCard className="mx-auto h-12 w-12 text-gray-400" />
          <h3 className="mt-2 text-sm font-semibold text-gray-900">
            No Transactions
          </h3>
          <p className="mt-1 text-sm text-gray-500">
            This tenant has no transactions yet.
          </p>
        </div>
      ) : (
        <div className="bg-white shadow-sm ring-1 ring-gray-900/5 rounded-lg overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('id')}>
                    <div className="flex items-center space-x-1">
                      <span>Transaction ID</span>
                      {getSortIcon('id')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('amount')}>
                    <div className="flex items-center space-x-1">
                      <span>Amount</span>
                      {getSortIcon('amount')}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('created_date')}>
                    <div className="flex items-center space-x-1">
                      <span>Created Date</span>
                      {getSortIcon('created_date')}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {billableEvents.map(event => (
                  <tr key={event.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-8 w-8 bg-primary/10 rounded-full flex items-center justify-center">
                          <CreditCard className="h-4 w-4 text-primary" />
                        </div>
                        <div className="ml-3">
                          <div className="text-sm font-medium text-gray-900 font-mono">
                            {event.id}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">
                        {formatAmount(event.amount, event.currency)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {event.created_date ? (
                        <div className="flex items-center">
                          <Calendar className="w-4 h-4 mr-1 text-gray-400" />
                          {new Date(event.created_date).toLocaleDateString(
                            'en-US',
                            {
                              year: 'numeric',
                              month: 'short',
                              day: 'numeric',
                            },
                          )}
                        </div>
                      ) : (
                        <span className="text-gray-400">-</span>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleViewResource(event)}>
                        <Eye className="w-4 h-4 mr-1" />
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="bg-white px-4 py-3 border-t border-gray-200 sm:px-6">
            <div className="flex items-center justify-between">
              <div className="text-sm text-gray-700">
                {(() => {
                  const startItem = (currentPage - 1) * 20 + 1;
                  const endItem = startItem + billableEvents.length - 1;
                  const total = pagination?.total || 0;

                  if (total === 0 && !isLoading) {
                    return 'No transactions found';
                  }

                  if (billableEvents.length === 0) {
                    return 'Loading...';
                  }

                  return `Viewing ${startItem}-${endItem} of ${total} total transactions`;
                })()}
              </div>
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1 || isLoading}>
                  Previous
                </Button>
                <span className="text-sm text-gray-700">
                  Page {currentPage}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleNextPage}
                  disabled={!pagination?.hasNextPage || isLoading}>
                  {isLoading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Loading...
                    </>
                  ) : (
                    'Next'
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Partner Resource Modal */}
      {showResourceModal && selectedTransaction && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-50 transition-opacity"
            onClick={() => {
              setShowResourceModal(false);
              setSelectedTransaction(null);
            }}
          />

          {/* Dialog */}
          <div className="flex min-h-full items-center justify-center p-4">
            <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
              {/* Header */}
              <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-semibold text-gray-900">
                    Transaction Details
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    Transaction ID: {selectedTransaction.id}
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowResourceModal(false);
                    setSelectedTransaction(null);
                  }}
                  className="text-gray-400 hover:text-gray-500">
                  <X className="w-5 h-5" />
                </button>
              </div>

              {/* Loading State */}
              {!selectedSchemaId && transactionSchemas.length > 1 && (
                <div className="flex-1 flex items-center justify-center p-6">
                  <div className="text-center">
                    <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
                  </div>
                </div>
              )}

              {/* Content */}
              {selectedSchemaId && (
                <div className="flex-1 overflow-y-auto p-6">
                  <PartnerResourceModal
                    tenantId={tenantId}
                    resource={{
                      externalId: selectedTransaction.id,
                      displayName: `Transaction ${selectedTransaction.id}`,
                    }}
                    isOpen={true}
                    onClose={() => {
                      setShowResourceModal(false);
                      setSelectedTransaction(null);
                      setSelectedSchemaId('');
                    }}
                    schemaDefinitionId={selectedSchemaId}
                  />
                </div>
              )}

              {/* No Schema Found */}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
